import AssetTemplateReadDto, { Groups, Endpoints, Gateways } from '../../../Models/Assets/AssetTemplateReadDto';

// Build updated template from current state
export const buildUpdatedTemplate = (
  templateData: any,
  groups: Groups[],
  endpoints: Endpoints[],
  gateways: Gateways[]
): AssetTemplateReadDto => {
  return {
    id: templateData.id,
    name: templateData.name,
    description: templateData.description,
    imageId: templateData.imageId,
    isGreasePumpConnectivityEnabled: templateData.isGreasePumpConnectivityEnabled || false,
    groups: groups.map(group => ({
      ...group,
      parentId: group.parentId || '',
      imageId: group.imageId || '',
      status: group.status ?? 'Active',
      attachmentImageIds: group.attachmentImageIds || []
    })),
    endpoints: endpoints.map(endpoint => ({
      ...endpoint,
      endpointDeviceId: endpoint.endpointDeviceId || '',
      endpointDeviceSerialNumber: endpoint.endpointDeviceSerialNumber || '',
      requirementId: endpoint.requirementId || '',
      requirementName: endpoint.requirementName || '',
      xPos: endpoint.xPos || 0,
      yPos: endpoint.yPos || 0,
      status: (endpoint as any).status ?? 'Active'
    })),
    gateways: gateways.map(gw => ({
      ...gw,
      status: gw.status ?? 'Active'
    }))
  };
};

// Check if template has changed
export const hasTemplateChanged = (
  updatedTemplate: AssetTemplateReadDto,
  templateData: any
): boolean => {
  return JSON.stringify(updatedTemplate) !== JSON.stringify(templateData);
};

// Get columns display position based on screen size (4 tiles: Image, Gateways, Groups, Endpoints)
export const getColumnsDisplayPosition = (isSingleColumn: boolean) => {
  return isSingleColumn
    ? [
        { col: 1, colSpan: 1, rowSpan: 21 },
        { col: 1, colSpan: 1, rowSpan: 9 },
        { col: 1, colSpan: 1, rowSpan: 16 },
        { col: 1, colSpan: 1, rowSpan: 14 },
      ]
    : [
        { col: 1, colSpan: 1, rowSpan: 21 },
        { col: 1, colSpan: 1, rowSpan: 9 },
        { col: 2, colSpan: 1, rowSpan: 16 },
        { col: 2, colSpan: 1, rowSpan: 14 },
      ];
};


// Groups deleted in the template must not show up on the asset (nor their endpoints)
export const isDeletedGroup = (group: Groups): boolean => group.status === 'Deleted';

export const withoutDeletedGroups = (groups: Groups[]): Groups[] =>
  groups.filter(group => !isDeletedGroup(group));

export const withoutDeletedGroupEndpoints = (endpoints: Endpoints[], groups: Groups[]): Endpoints[] => {
  const deletedGroupIds = new Set(groups.filter(isDeletedGroup).map(group => group.id));
  return endpoints.filter(endpoint => !deletedGroupIds.has(endpoint.groupId));
};

export const onlyDeletedGroupEndpoints = (endpoints: Endpoints[], groups: Groups[]): Endpoints[] => {
  const deletedGroupIds = new Set(groups.filter(isDeletedGroup).map(group => group.id));
  return endpoints.filter(endpoint => deletedGroupIds.has(endpoint.groupId));
};


// The backend only processes Active endpoints, so one under a disabled group must never be sent as Active:
// its point may no longer exist and the whole save fails. Applied at submit, not on group toggle, so groups
// that were already disabled when the dialog opened get reconciled too.
export const isDisabledGroup = (group: Groups): boolean =>
  group.status === 'Inactive' || group.status === 'Deleted';

export const applyGroupStatusToEndpoints = (endpoints: Endpoints[], groups: Groups[]): Endpoints[] => {
  const disabledGroupIds = new Set(groups.filter(isDisabledGroup).map(group => group.id));
  return endpoints.map(endpoint =>
    disabledGroupIds.has(endpoint.groupId) && endpoint.status !== 'Deleted'
      ? { ...endpoint, status: 'Inactive' as const }
      : endpoint
  );
};
